import React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { useSectionContext } from '../layout/Section';
import { ANIMATION_DURATION, ANIMATION_EASING, STAGGER_INCREMENT } from '../../hooks/useScrollAnimation';

type AnimationVariant = 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight' | 'scaleIn' | 'blurReveal';

// Reduced motion variants - instant opacity changes only
const reducedMotionVariants: Record<AnimationVariant, Variants> = {
  fadeUp: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideLeft: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideRight: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  scaleIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  blurReveal: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
};

interface AnimatedElementProps {
  children: React.ReactNode;
  variant?: AnimationVariant;
  index?: number; // For staggered animations
  delay?: number; // Additional delay
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

// Animation variants
const animationVariants: Record<AnimationVariant, Variants> = {
  fadeUp: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 },
  },
  scaleIn: {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
  },
  blurReveal: {
    hidden: { opacity: 0, filter: 'blur(10px)', y: 20 },
    visible: { opacity: 1, filter: 'blur(0px)', y: 0 },
  },
};

/**
 * Animated element wrapper that responds to parent Section visibility
 * - Staggered animations based on index
 * - Multiple animation variants
 * - Integrates with Section context
 */
const AnimatedElement: React.FC<AnimatedElementProps> = ({
  children,
  variant = 'fadeUp',
  index = 0,
  delay = 0,
  className = '',
  as = 'div',
}) => {
  const { isVisible } = useSectionContext();
  const prefersReducedMotion = useReducedMotion();

  const totalDelay = delay + index * STAGGER_INCREMENT;

  const MotionComponent = motion[as as keyof typeof motion] as typeof motion.div;

  // Use reduced motion variants if user prefers
  const variants = prefersReducedMotion ? reducedMotionVariants[variant] : animationVariants[variant];
  const duration = prefersReducedMotion ? 0.01 : ANIMATION_DURATION.normal;
  const computedDelay = prefersReducedMotion ? 0 : (isVisible ? totalDelay : 0);

  return (
    <MotionComponent
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={variants}
      transition={{
        duration,
        ease: ANIMATION_EASING,
        delay: computedDelay,
      }}
      className={className}
    >
      {children}
    </MotionComponent>
  );
};

/**
 * Container for staggered children animations
 * Automatically applies stagger delays to direct motion children
 */
export const StaggerContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
}> = ({ children, className = '', staggerDelay = STAGGER_INCREMENT }) => {
  const { isVisible } = useSectionContext();
  const prefersReducedMotion = useReducedMotion();

  const containerVariants: Variants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : staggerDelay,
        delayChildren: prefersReducedMotion ? 0 : 0.1,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={containerVariants}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Child item for use within StaggerContainer
 */
export const StaggerItem: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: AnimationVariant;
}> = ({ children, className = '', variant = 'fadeUp' }) => {
  return (
    <motion.div variants={animationVariants[variant]} className={className}>
      {children}
    </motion.div>
  );
};

export default AnimatedElement;
